import { Drawer } from "antd";
import { isEmpty, isEqual, isNil } from "lodash";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import Button from "shared/components/Button";
import { NAME_SPACES } from "shared/locales/constants";
import { zonesOf } from "../../field-status";
import { usesFromUsages } from "utils/helpers/documentTemplates";
import { completeUses, meaningfulMappings } from "./draft-content";
import { usageText } from "./usage-line";
import { zoneLabel, zoneMappingKeys } from "./zones";

const FIELD_CHANGES = {
  ADDED: "FIELD_ADDED",
  CHANGED: "FIELD_CHANGED",
  REMOVED: "FIELD_REMOVED",
};

const comparableUses = (uses) => usesFromUsages(completeUses(uses));

const fieldChanges = ({ content, template }) => {
  const before = zonesOf(template.anchors || []);
  const after = zonesOf(content.anchors);
  const beforeIds = new Set(before.map((zone) => zone.id));
  const afterIds = new Set(after.map((zone) => zone.id));
  const previous = meaningfulMappings(template.anchorMappings);
  const next = meaningfulMappings(content.anchorMappings);
  return [
    ...after
      .filter((zone) => !beforeIds.has(zone.id))
      .map((zone) => ({ zone, change: FIELD_CHANGES.ADDED })),
    ...after
      .filter(
        (zone) =>
          beforeIds.has(zone.id) &&
          zoneMappingKeys(zone).some(
            (key) => !isEqual(previous[key], next[key]),
          ),
      )
      .map((zone) => ({ zone, change: FIELD_CHANGES.CHANGED })),
    ...before
      .filter((zone) => !afterIds.has(zone.id))
      .map((zone) => ({ zone, change: FIELD_CHANGES.REMOVED })),
  ];
};

const publishChanges = ({ content, template, pendingFile }) => {
  const uses = comparableUses(content.uses);
  return {
    name:
      (content.name || "") === (template.name || "")
        ? null
        : { before: template.name || "", after: content.name || "" },
    file: pendingFile,
    uses: isEqual(uses, comparableUses(usesFromUsages(template.usages)))
      ? null
      : uses,
    fields: fieldChanges({ content, template }),
  };
};

const PublishSheet = ({
  open,
  content,
  template,
  pendingFile,
  planNames,
  testedWithRealProject,
  publishing,
  onClose,
  onPublish,
  onChooseProduct,
  onTestReal,
}) => {
  const { t } = useTranslation(
    NAME_SPACES.PRIVATE.ADMIN.DYNAMIC_DOCUMENTS_EDITOR,
  );
  const PUBLISH = t("PUBLISH", { returnObjects: true });
  const changes = useMemo(
    () => publishChanges({ content, template, pendingFile }),
    [content, template, pendingFile],
  );
  const uses = completeUses(content.uses);
  const unchanged =
    isNil(changes.name) &&
    isNil(changes.file) &&
    isNil(changes.uses) &&
    isEmpty(changes.fields);

  return (
    <Drawer
      open={open}
      width={480}
      title={PUBLISH.TITLE}
      onClose={onClose}
      rootClassName="dynamic-documents-editor__sheet"
      footer={
        <div className="dynamic-documents-editor__sheet-footer">
          <Button type="gray--link" onClick={onClose} disabled={publishing}>
            {PUBLISH.CANCEL}
          </Button>
          <Button type="primary" loading={publishing} onClick={onPublish}>
            {PUBLISH.CONFIRM}
          </Button>
        </div>
      }
    >
      <h4 className="dynamic-documents-editor__step-title">
        {PUBLISH.CHANGES}
      </h4>
      {unchanged ? (
        <p className="dynamic-documents-editor__muted">{PUBLISH.NO_CHANGE}</p>
      ) : (
        <ul className="dynamic-documents-editor__changes">
          {!isNil(changes.name) && <li>{t("PUBLISH.NAME", changes.name)}</li>}
          {!isNil(changes.file) && (
            <li>{t("PUBLISH.NEW_PDF", { name: changes.file.name })}</li>
          )}
          {!isNil(changes.uses) && (
            <li>
              {t("PUBLISH.USES", {
                uses: isEmpty(changes.uses)
                  ? t("USES.NONE")
                  : changes.uses
                      .map((use) => usageText(use, planNames, t))
                      .join(", "),
              })}
            </li>
          )}
          {changes.fields.map(({ zone, change }) => (
            <li key={`${change}-${zone.id}`}>
              {zoneLabel(zone, t)}{" "}
              <span className="dynamic-documents-editor__change-tag">
                {PUBLISH[change]}
              </span>
            </li>
          ))}
        </ul>
      )}
      <p className="dynamic-documents-editor__effect">{PUBLISH.EFFECT}</p>
      <h4 className="dynamic-documents-editor__step-title">{PUBLISH.WHERE}</h4>
      {isEmpty(uses) ? (
        <div className="dynamic-documents-editor__amber">
          {PUBLISH.NO_PRODUCT}{" "}
          <span className="dynamic-documents-editor__dot" />{" "}
          <button
            type="button"
            className="dynamic-documents-editor__link"
            onClick={onChooseProduct}
          >
            {PUBLISH.CHOOSE_PRODUCT}
          </button>
        </div>
      ) : (
        <ul className="dynamic-documents-editor__changes">
          {uses.map((use, index) => (
            <li key={index}>{usageText(use, planNames, t)}</li>
          ))}
        </ul>
      )}
      {!testedWithRealProject && (
        <div className="dynamic-documents-editor__amber">
          {PUBLISH.SAMPLES_ONLY}{" "}
          <span className="dynamic-documents-editor__dot" />{" "}
          <button
            type="button"
            className="dynamic-documents-editor__link"
            onClick={onTestReal}
          >
            {PUBLISH.TEST_REAL}
          </button>
        </div>
      )}
    </Drawer>
  );
};

export default PublishSheet;
